import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { cardName } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULTS = { q: "", lang: "all", type: "all", house: "all", rarity: "all", min: "", max: "", sort: "recent" };
const KEYS = Object.keys(DEFAULTS);

const rarityOf = (p) => p.yugioh_rarity || p.card?.rarity || "";
const kindOf = (p) => (p.variant === "bundle" || p.card?.is_bundle ? "bundles" : p.variant === "sealed" || p.card?.is_sealed ? "sealed" : "cards");
const priceOf = (p) => Number(p.buyer_pays ?? p.price ?? 0) || 0;
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function useSellerFilters(pubs) {
  const [params, setParams] = useSearchParams();
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) ?? DEFAULTS[k]]));
  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === DEFAULTS[k] || v === "") next.delete(k); else next.set(k, v);
    if (k === "type" && v !== "graded") next.delete("house");
    setParams(next, { replace: true });
  };
  const reset = () => {
    const next = new URLSearchParams(params);
    KEYS.forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };
  const rarities = useMemo(() => [...new Set(pubs.map(rarityOf).filter(Boolean))].sort(), [pubs]);
  const result = useMemo(() => {
    const q = norm(f.q).trim();
    const min = parseFloat(f.min), max = parseFloat(f.max);
    const out = pubs.filter((p) => {
      if (q) {
        const hay = norm([cardName(p.card, p.language), p.card?.name_es, p.card?.name_en, p.card?.set_name, p.yugioh_set_name, p.yugioh_set_code, p.card_id].join(" "));
        if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
      }
      if (f.lang !== "all" && (p.language || "es") !== f.lang) return false;
      if (f.type === "graded") {
        if (!p.graded) return false;
        if (f.house !== "all" && p.graded_house !== f.house) return false;
      } else if (f.type !== "all" && kindOf(p) !== f.type) return false;
      if (f.rarity !== "all" && rarityOf(p) !== f.rarity) return false;
      if (!isNaN(min) && priceOf(p) < min) return false;
      if (!isNaN(max) && priceOf(p) > max) return false;
      return true;
    });
    if (f.sort === "price_asc") out.sort((a, b) => priceOf(a) - priceOf(b));
    else if (f.sort === "price_desc") out.sort((a, b) => priceOf(b) - priceOf(a));
    else out.sort((a, b) => String(b.listed_at || b.created_at || "").localeCompare(String(a.listed_at || a.created_at || "")));
    return out;
  }, [pubs, f.q, f.lang, f.type, f.house, f.rarity, f.min, f.max, f.sort]);
  const active = KEYS.filter((k) => k !== "sort" && f[k] !== DEFAULTS[k]).length;
  return { f, set, reset, rarities, result, active };
}

const Sel = ({ value, onChange, testid, children }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-[#171717] border-[#262626] text-white h-10 text-sm"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-[#171717] border-[#262626] text-white max-h-72 overflow-y-auto">{children}</SelectContent>
  </Select>
);

const inputCls = "bg-[#171717] border border-[#262626] text-white rounded-md px-3 h-10 text-sm focus:outline-none focus:border-[#EF4444]";

export function SellerFiltersBar({ ctl, total }) {
  const { f, set, reset, rarities, result, active } = ctl;
  return (
    <div className="mb-4 space-y-2" data-testid="seller-filters">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Buscar en este catálogo…" data-testid="seller-search" className={`${inputCls} w-full pl-9 h-11`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Sel value={f.lang} onChange={(v) => set("lang", v)} testid="seller-filter-language">
          <SelectItem value="all">Todos los idiomas</SelectItem>
          <SelectItem value="es">Español</SelectItem>
          <SelectItem value="en">Inglés</SelectItem>
          <SelectItem value="ja">Japonés</SelectItem>
          <SelectItem value="otro">Otro</SelectItem>
        </Sel>
        <Sel value={f.type} onChange={(v) => set("type", v)} testid="seller-filter-type">
          <SelectItem value="all">Todos los tipos</SelectItem>
          <SelectItem value="cards">Solo cartas</SelectItem>
          <SelectItem value="sealed">Solo sellados</SelectItem>
          <SelectItem value="bundles">Solo colecciones</SelectItem>
          <SelectItem value="graded">Solo gradeadas</SelectItem>
        </Sel>
        {f.type === "graded" && (
          <Sel value={f.house} onChange={(v) => set("house", v)} testid="seller-filter-house">
            <SelectItem value="all">Todas las casas</SelectItem>
            {["PSA", "BGS", "CGC", "SGC"].map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </Sel>
        )}
        {rarities.length > 0 && f.type !== "sealed" && (
          <Sel value={f.rarity} onChange={(v) => set("rarity", v)} testid="seller-filter-rarity">
            <SelectItem value="all">Todas las rarezas</SelectItem>
            {rarities.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </Sel>
        )}
        <input type="number" min="0" inputMode="decimal" placeholder="Desde $" value={f.min} onChange={(e) => set("min", e.target.value)} data-testid="seller-filter-min" className={inputCls} />
        <input type="number" min="0" inputMode="decimal" placeholder="Hasta $" value={f.max} onChange={(e) => set("max", e.target.value)} data-testid="seller-filter-max" className={inputCls} />
        <Sel value={f.sort} onChange={(v) => set("sort", v)} testid="seller-filter-sort">
          <SelectItem value="recent">Más recientes</SelectItem>
          <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
          <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
        </Sel>
      </div>
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span data-testid="seller-filter-count">{result.length} de {total} publicaciones</span>
        {active > 0 && <button onClick={reset} className="inline-flex items-center gap-1 text-[#EF4444] hover:underline" data-testid="seller-filter-reset"><X size={12} /> Limpiar filtros ({active})</button>}
      </div>
    </div>
  );
}
